import { useEffect, useState } from "react";
import styles from "../../styles/components/LeadCard.module.css";
import cardBackgroundLight from "../../assets/images/card_background.svg";
import cardBackgroundDark from "../../assets/images/card_background_dark.svg";
import { ReactComponent as VectorArrowIcon } from "../../assets/images/vector_arrow.svg";
import Avatar from "../../ui/avatar/Avatar";

function formatResponsibleName(fullName) {
  if (!fullName) return "";

  const parts = fullName.trim().split(" ").filter(Boolean);

  if (parts.length === 1) {
    return parts[0];
  }

  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0].toUpperCase();

  return `${firstName} ${lastInitial}.`;
}

function getCompanyNameToShow(companyName, leadName) {
  const trimmedCompanyName = (companyName ?? "").trim();
  if (!trimmedCompanyName) return "";

  const isSameAsLeadName =
    trimmedCompanyName.localeCompare((leadName ?? "").trim(), "es", {
      sensitivity: "base",
    }) === 0;

  return isSameAsLeadName ? "" : trimmedCompanyName;
}

function getCurrentTheme() {
  return document.documentElement.getAttribute("data-theme") === "dark"
    ? "dark"
    : "light";
}

const LeadCard = ({
  responsible,
  id,
  hot,
  lead_name,
  company,
  salutation,
  first_name,
  middle_name,
  last_name,
  account,
  onOpen,
  theme,
  selected = false,
  selectable = true,
  onToggleSelect,
  has_unread_mail = false,
  has_due_reminder = false,
}) => {
  const [internalTheme, setInternalTheme] = useState(getCurrentTheme);
  const resolvedTheme = theme ?? internalTheme;

  useEffect(() => {
    if (theme) return undefined;

    const observer = new MutationObserver(() => {
      setInternalTheme(getCurrentTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, [theme]);

  const companyName = getCompanyNameToShow(company?.name, lead_name);

  const backgroundImage =
    resolvedTheme === "dark" ? cardBackgroundDark : cardBackgroundLight;

  const handleCardClick = () => {
    if (!selectable) return;
    onToggleSelect?.();
  };

  return (
    <div
      className={`${styles.leadCard} ${
        selected ? styles.leadCardSelected : ""
      } ${selectable ? "" : styles.leadCardDisabled}`}
      style={{ color: account?.color || "inherit" }}
      role="button"
      tabIndex={selectable ? 0 : -1}
      aria-pressed={selected}
      aria-disabled={!selectable}
      onClick={handleCardClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      }}
    >
      <img className={styles.leadCardChild} alt="" src={backgroundImage} />

      <div className={styles.frameParent}>
        <div className={styles.frameGroup}>
          <div className={styles.frameWrapper}>
            <div className={styles.frameContainer}>
              <div className={styles.clientsPillParent}>
                <div
                  className={styles.clientsPill}
                  style={
                    account?.color
                      ? { background: account.color }
                      : undefined
                  }
                >
                  <div className={styles.huawei}>{account?.name}</div>
                </div>

                {hot && (
                  <div className={styles.hot}>
                    <div>🔥</div>
                  </div>
                )}

                {(has_unread_mail || has_due_reminder) && (
                  <span className={styles.statusDots}>
                    {has_unread_mail && (
                      <span
                        className={styles.unreadDot}
                        role="status"
                        aria-label="Tiene correos sin leer"
                      />
                    )}
                    {has_due_reminder && (
                      <span
                        className={styles.reminderDot}
                        role="status"
                        aria-label="Tiene un recordatorio vencido"
                      />
                    )}
                  </span>
                )}
              </div>

              <div className={styles.leadName}>{lead_name}</div>

              {companyName && (
                <div className={styles.companyName} title={companyName}>
                  {companyName}
                </div>
              )}
            </div>
          </div>

          <div className={styles.recipientName}>
            {salutation} {first_name} {middle_name} {last_name}
          </div>
        </div>

        <div className={styles.frameDiv}>
          <div className={styles.userParent}>
            <div className={styles.user}>
              <Avatar
                src={
                  responsible?.user_photo ||
                  responsible?.photo ||
                  responsible?.avatar ||
                  responsible?.image ||
                  null
                }
                name={responsible?.name}
                size={20}
              />
            </div>
            <div className={styles.responsable}>
              {formatResponsibleName(responsible?.name)}
            </div>
          </div>

          <div className={styles.div1}>#{id}</div>
        </div>
      </div>
      {!selected && (
        <div
          className={styles.modalButton}
          role="button"
          tabIndex={0}
          aria-label="Abrir lead"
          onClick={(event) => {
            event.stopPropagation();
            onOpen?.();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              onOpen?.();
            }
          }}
        >
          <VectorArrowIcon className={styles.modalButtonChild} />
        </div>
      )}
    </div>
  );
};

export default LeadCard;
